import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { Input } from '../components/ui/Field';
import { errorMessage } from '../lib/api';

/** Only allow relative in-app redirects. */
export function safeNext(value: string | null): string {
  return value && value.startsWith('/') && !value.startsWith('//') ? value : '/';
}

export function LoginPage() {
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [form, setForm] = useState({ login: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  if (status === 'authenticated' && !pending) return <Navigate to={next} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      await login(form);
      navigate(next, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="mx-auto max-w-sm pt-8">
      <h1 className="mb-6 text-center text-4xl">
        Log<span className="text-accent">in</span>
      </h1>
      <Card>
        <CardBody>
          <form onSubmit={submit} className="space-y-4">
            <Input
              label="Benutzername oder E-Mail"
              autoComplete="username"
              autoFocus
              required
              value={form.login}
              onChange={(e) => setForm({ ...form, login: e.target.value })}
            />
            <Input
              label="Passwort"
              type="password"
              autoComplete="current-password"
              required
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
            {error && (
              <p role="alert" className="text-sm text-danger">
                {error}
              </p>
            )}
            <Button type="submit" className="w-full" loading={pending}>
              Einloggen
            </Button>
          </form>
        </CardBody>
      </Card>
      <p className="mt-4 text-center text-xs text-ink-400">
        Registrierung nur per Einladungslink eines Admins.
      </p>
    </div>
  );
}
